import React from "react";
import { useTranslation } from "react-i18next";
import PrivacyIcon from "../../../../assets/images/png/privacy_icon.png";
import { Image } from "../../../../components/__common/Image";
import { Typography } from "../../../../components/__common/Typography/Typography";
import {
  LinkButton,
  TTSButton,
} from "../../../../components/__common/_controls";
import { SVG_ICONS } from "../../../../components/__common/Svg/Svg";
import {
  EVENT_ACTION_PP_DOWNLOAD_PRIVACY_POLICY,
  EVENT_CATEGORY_PRIVACY_POLICY,
} from "../../../../constants/analytics";
import GoogleAnalytics from "../../../../utils/GoogleAnalytics";

interface PrivacyPolicyHeaderProps {
  hasVersionHistory: boolean;
  ppSource: string;
  ppSourceLocal?: string;
  hasLPP?: boolean;
  langCode?: string;
  countryCode?: string;
  onVersionHistoryClick: () => void;
  onLanguageClick: () => void;
}

/**
 * PrivacyPolicyHeader component
 * Displays the title section with action buttons
 */
export const PrivacyPolicyHeader: React.FC<PrivacyPolicyHeaderProps> = ({
  hasVersionHistory,
  ppSource,
  ppSourceLocal,
  hasLPP,
  langCode,
  countryCode,
  onVersionHistoryClick,
  onLanguageClick,
}) => {
  const { t } = useTranslation();

  const formatFilename = (
    policyType: "Global" | "Local",
    lang: string,
    country: string
  ): string => {
    return `SamsungPrivacyPolicy_${policyType}_${lang}-${country}.html`;
  };

  const downloadFile = async (url: string, filename: string): Promise<void> => {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleDownloadClick = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_PRIVACY_POLICY,
      EVENT_ACTION_PP_DOWNLOAD_PRIVACY_POLICY
    );

    if (!hasLPP || !ppSourceLocal || !langCode || !countryCode) {
      const globalFilename = formatFilename(
        "Global",
        langCode || "en",
        countryCode || "US"
      );
      await downloadFile(ppSource, globalFilename);
      return;
    }

    try {
      const globalFilename = formatFilename("Global", langCode, countryCode);
      const localFilename = formatFilename("Local", langCode, countryCode);

      await downloadFile(ppSource, globalFilename);
      await new Promise((resolve) => setTimeout(resolve, 75));
      await downloadFile(ppSourceLocal, localFilename);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div className="privacy-policy__title-section">
      <Image image={PrivacyIcon} width={48} height={48} />
      <Typography variant="h2" className="privacy-policy__title-section__title">
        {t("PRIVACY_POLICY_GPP_PAGE_TITLE")}
        <TTSButton value={t("PRIVACY_POLICY_GPP_PAGE_TITLE")} />
      </Typography>

      <div className="privacy-policy__title-section__buttons">
        {hasVersionHistory && (
          <LinkButton
            id="version-history-button"
            testId="toggle-version-history-modal"
            to="#"
            textClassName="privacy-policy__title-section__buttons__version-history"
            title={t("PRIVACY_POLICY_GPP_BTN_VERSION_HISTORY")}
            icon={SVG_ICONS.PRIVACY_POLICY_HISTORY}
            iconPosition="left"
            iconSize={24}
            underlined={false}
            onClick={onVersionHistoryClick}
            svgIconToolTip={t("PRIVACY_POLICY_GPP_VERSION_HISTORY_POPUP_TITLE")}
          />
        )}

        <LinkButton
          id="policy-language-button"
          to="#"
          testId="pp-gpp-button-change-lang"
          textClassName="privacy-policy__title-section__buttons__language"
          title={t("PRIVACY_POLICY_GPP_BTN_CHANGE_LANGUAGE")}
          icon={SVG_ICONS.PRIVACY_POLICY_LANGUAGE}
          iconPosition="left"
          iconSize={24}
          underlined={false}
          onClick={onLanguageClick}
          svgIconToolTip={t("PRIVACY_POLICY_GPP_BTN_CHANGE_LANGUAGE")}
        />

        <LinkButton
          to="#"
          testId="trigger-download-button"
          textClassName="privacy-policy__title-section__buttons__download"
          title={t("PRIVACY_POLICY_GPP_BTN_DOWNLOAD")}
          icon={SVG_ICONS.PRIVACY_POLICY_DOWNLOAD}
          iconPosition="left"
          iconSize={24}
          underlined={false}
          onClick={handleDownloadClick}
          svgIconToolTip={t("PRIVACY_POLICY_GPP_BTN_DOWNLOAD")}
        />
      </div>
    </div>
  );
};
